/*
  Keeps the PDP carousel at the right height after the buy box swaps its media.

  The bug
  -------
  buy-box.js calls media-gallery.showBundleMedia() when a bag stepper crosses zero
  -- picking up a coffee that was at 0, or dropping one to 0. The gallery is then
  re-rendered, and the rebuilt image comes back absolutely positioned. An absolutely
  positioned image gives its slide no intrinsic height, so every slide stretches to
  the tallest in the set -- and three of the ten media in each coffee's set are
  square lifestyle cards. The carousel goes square, object-fit: cover crops the bag,
  and <slideshow-container> stops tracking the first media's aspect.

  Measured on the live PDP, clicking the one stepper sitting at zero: 11 slides and
  an 823px container before; 10 slides and 905px after, with the first media
  rendering at 1.001 while still declaring --ratio: 1.102. It has been there since
  launch. It takes a zero-crossing to show, which is why it survived this long --
  stepping a coffee that is already in the box does nothing at all.

  The fix
  -------
  Publish the first media's aspect as --heist-gallery-ratio on the gallery, pin the
  container to it, and let heist-gallery-fit.css pin every slide to the same value.
  One ratio for the whole carousel is what the native gallery settles at, and it is
  what stops the carousel changing height as you scroll through it.

  Why it is written this way
  --------------------------
  This holds no per-element state and no long-lived element references, because the
  <media-gallery> element does not survive the re-render. It is replaced by a morph
  that copies attributes across -- a data- flag set on it is still there afterwards,
  which is what made this so misleading -- while every JS property and every
  MutationObserver registered on the old node is silently dropped. That single fact
  explains two earlier attempts: an observer that never fired even as the slide
  count went 11 -> 10, and a start function stored on the element that read back
  undefined. A class survives, since classes are attributes; nothing else does.

  So every pass re-queries the document, and the break is detected by measurement
  rather than inference: does the first slide's rendered aspect still match the
  --ratio it declares? The DOM cannot answer that dishonestly. (Two structural
  guesses came first and both were wrong -- the rebuilt slides keep the inline
  --grid-template-rows Liquid gave them, so their absence is not the marker either.
  That same discovery is why the CSS now overrides those rows rather than restoring
  them: they were never lost, and left alone they hold a 50dvh floor under a
  correctly-sized image.)

  The poll stops on a measurement, not a countdown. The re-render is not atomic, so
  every fixed delay tried here landed in a gap and silently no-opped. Passes are
  idempotent, so repeats cost nothing.

  Not addressed: the rebuilt slides also lose product-media-container--zoomable and
  its declarative on:click binding, so click-to-zoom stops working after a swap.
  Restoring the class alone would give a zoom cursor with no dialog behind it. That
  belongs in the buy-box source, not in a patch.

  The real home for all of this is the vite repo, next to showBundleMedia. This is
  the version that survives a theme redeploy in the meantime.
*/
(function () {
  var POLL_MS = 100;
  var MAX_PASSES = 30; // ~3s, then give up rather than spin
  var ASPECT_TOLERANCE = 0.02;
  var TOLERANCE_PX = 8;

  // Sticky across re-renders: once corrected the symptom is gone, but the rule has
  // to stay applied or it comes straight back on the next swap.
  var broken = false;

  // What the first slide declares, and what it is actually rendering at.
  function reading(gallery) {
    var media = gallery.querySelector('slideshow-slide .product-media');
    if (!media) return null;
    var ratio = parseFloat(media.style.getPropertyValue('--ratio'));
    if (!(ratio > 0)) return null;
    var box = media.getBoundingClientRect();
    if (!(box.width > 0) || !(box.height > 0)) return null;
    return { ratio: ratio, actual: box.width / box.height, height: box.height };
  }

  function fitOne(gallery) {
    var before = reading(gallery);
    if (!before) return false;

    if (!broken) {
      if (Math.abs(before.actual - before.ratio) <= ASPECT_TOLERANCE) return false;
      broken = true;
    }

    // Publish the ratio first: the CSS below reads it for every slide, so the class
    // and the value have to land together or the first paint uses the fallback.
    gallery.style.setProperty('--heist-gallery-ratio', String(before.ratio));
    gallery.classList.add('heist-gallery-swapped');

    var container = gallery.querySelector('slideshow-container');
    if (!container) return false;

    // Re-read: the class above changes layout, and getBoundingClientRect is
    // synchronous, so this is the corrected geometry rather than the stale one.
    var after = reading(gallery);
    if (!after) return false;

    // Size the container to the media it ACTUALLY holds, not to its own width over
    // the media ratio. Those are the same number only while the slide fills the
    // container, which is a desktop-only assumption: under 750px the slide is
    // 81.067% wide so the next one peeks in, and a ratio-sized container reserved
    // ~67px of empty band under the carousel on every phone. Deriving the ratio
    // from the measured height is breakpoint-agnostic, and the resize handler at
    // the bottom of this file re-runs it whenever the slide width rule changes.
    var containerWidth = container.getBoundingClientRect().width;
    if (containerWidth > 0 && after.height > 0) {
      container.style.aspectRatio = String(containerWidth / after.height);
    }

    return (
      Math.abs(after.actual - after.ratio) <= ASPECT_TOLERANCE &&
      Math.abs(container.getBoundingClientRect().height - after.height) <= TOLERANCE_PX
    );
  }

  function fit() {
    var galleries = document.querySelectorAll('media-gallery');
    if (!galleries.length) return false;
    var settled = true;
    galleries.forEach(function (gallery) {
      if (!fitOne(gallery)) settled = false;
    });
    return settled;
  }

  var poller = null;
  function startPolling() {
    if (poller) clearInterval(poller);
    var passes = 0;
    poller = setInterval(function () {
      passes++;
      if (fit() || passes >= MAX_PASSES) {
        clearInterval(poller);
        poller = null;
      }
    }, POLL_MS);
  }

  // Only a stepper can trigger the re-render. Delegated on the document so it
  // survives the gallery being replaced, and capturing so it runs whether or not
  // the buy box stops the event on its way up.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest || !target.closest('[data-buy-box-stepper]')) return;
      startPolling();
    },
    true
  );

  // The container's height is width-dependent, so recheck after a resize.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(startPolling, 150);
  });
})();
